import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../lib/auth';
import { fetchJSON } from '../lib/api';
import {
  FaUsers, FaChartBar, FaBookOpen, FaClipboardList,
  FaArrowLeft, FaFloppyDisk, FaXmark, FaPen, FaKey,
  FaTrash, FaCheck, FaCircle, FaEye, FaReceipt, FaGift, FaGlobe,
} from 'react-icons/fa6';

const TABS = [
  { id: 'overview',   label: 'Overview',     icon: <FaChartBar /> },
  { id: 'users',      label: 'Users',         icon: <FaUsers /> },
  { id: 'progress',   label: 'Progress',      icon: <FaClipboardList /> },
  { id: 'lessons',    label: 'Edit Content',  icon: <FaBookOpen /> },
  { id: 'surveys',    label: 'Surveys',       icon: <FaClipboardList /> },
  { id: 'purchases',  label: 'Purchases',     icon: <FaReceipt /> },
  { id: 'pilots',     label: 'Access Codes',  icon: <FaKey /> },
  { id: 'domains',    label: 'Domain Access', icon: <FaGlobe /> },
];

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-ink/10 bg-white px-6 py-5">
      <div className="text-[11px] tracking-[0.18em] uppercase text-slate2">{label}</div>
      <div className="mt-1 font-display text-4xl text-ink">{value ?? '—'}</div>
      {sub && <div className="mt-1 text-xs text-slate2">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid:   'bg-brand-sage/30 text-brand-deep',
    pilot:  'bg-brand-aubergine/10 text-brand-aubergine',
    gifted: 'bg-blue-50 text-blue-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide ${map[status] || 'bg-ink/10 text-ink/60'}`}>
      {status || 'none'}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: OVERVIEW
// ────────────────────────────────────────────────────────────────────────────
function OverviewTab() {
  const [data, setData] = useState(null);
  const [completions, setCompletions] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchJSON('/api/admin/overview'),
      fetchJSON('/api/admin/completions'),
    ]).then(([overview, comp]) => {
      setData(overview);
      setCompletions(comp.completions);
    }).catch((e) => setErr(e.message));
  }, []);

  if (err) return <p className="text-red-700 text-sm">{err}</p>;
  if (!data || !completions) return <p className="text-slate2 text-sm">Loading…</p>;

  const courseDone = (row) => {
    const pct = parseInt(row.total_lessons, 10) > 0
      ? Math.round((parseInt(row.lessons_completed, 10) / parseInt(row.total_lessons, 10)) * 100)
      : 0;
    return { pct, completed: parseInt(row.lessons_completed, 10), total: parseInt(row.total_lessons, 10) };
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard label="Total users"        value={data.totalUsers} />
        <StatCard label="Enrollments"        value={data.totalEnrollments} />
        <StatCard label="Active (7 days)"    value={data.activeSessionsLast7Days} sub="unique users with live sessions" />
        <StatCard label="Survey responses"   value={data.surveyResponses} />
        <StatCard label="Code redemptions"    value={data.pilotRedemptions} />
        <StatCard label="Access codes"       value={data.pilotCodes} sub="active" />
      </div>

      <div>
        <div className="font-display text-lg text-ink mb-3">Enrolled Learner Progress</div>
        {completions.length === 0 ? (
          <p className="text-slate2 text-sm">No enrolled learners yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-ink/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink/10 bg-cream/60">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Learner</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Course</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Lessons</th>
                  <th className="text-center px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Completed</th>
                  <th className="text-center px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Survey</th>
                  <th className="text-center px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Certificate</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Enrolled</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/5">
                {completions.map((row) => {
                  const { pct, completed, total } = courseDone(row);
                  const courseComplete = completed >= total && total > 0;
                  return (
                    <tr key={`${row.id}-${row.course_slug}`} className="hover:bg-cream/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-ink">{row.name || <span className="italic text-slate2">—</span>}</div>
                        <div className="text-xs text-slate2">{row.email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate2 text-xs">{row.course_title}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-ink/10 rounded-full h-1.5 overflow-hidden flex-shrink-0">
                            <div
                              className={`h-1.5 rounded-full ${courseComplete ? 'bg-brand-deep' : 'bg-brand-sage'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate2 whitespace-nowrap">{completed}/{total}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {courseComplete
                          ? <FaCheck className="text-brand-deep mx-auto" />
                          : <span className="text-xs text-slate2">{pct}%</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.survey_done
                          ? <span title={fmtDate(row.survey_submitted_at)}><FaCheck className="text-brand-deep mx-auto" /></span>
                          : <span className="text-slate2/40 text-xs">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.cert_viewed
                          ? <span title={fmtDate(row.cert_viewed_at)}><FaCheck className="text-brand-deep mx-auto" /></span>
                          : <span className="text-slate2/40 text-xs">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-xs text-slate2 whitespace-nowrap">{fmtDate(row.purchased_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: USERS
// ────────────────────────────────────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchJSON('/api/admin/users').then((d) => setUsers(d.users)).catch((e) => setErr(e.message));
  }, []);

  const handleDelete = useCallback(async (userId) => {
    setDeleting(true);
    try {
      await fetchJSON(`/api/admin/users/${userId}`, { method: 'DELETE' });
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setConfirmDelete(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setDeleting(false);
    }
  }, []);

  if (err) return <p className="text-red-700 text-sm">{err}</p>;
  if (!users) return <p className="text-slate2 text-sm">Loading…</p>;

  const filtered = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="mb-4">
        <input
          type="search"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-sm rounded-xl border border-ink/15 bg-white px-4 py-2 text-sm focus:outline-none focus:border-brand-deep/60"
        />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-ink/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink/10 bg-cream/60">
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Name</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Email</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Status</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Progress</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Joined</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Last Login</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/5">
            {filtered.map((u) => (
              <tr key={u.id} className="hover:bg-cream/40 transition-colors">
                <td className="px-4 py-3 font-medium text-ink whitespace-nowrap">
                  {u.name || <span className="text-slate2 italic">—</span>}
                  {u.is_admin && (
                    <span className="ml-2 text-[10px] bg-brand-deep text-bone px-1.5 py-0.5 rounded uppercase tracking-wide">admin</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate2">{u.email}</td>
                <td className="px-4 py-3"><StatusBadge status={u.purchase_status} /></td>
                <td className="px-4 py-3 text-right text-slate2">{u.lessons_completed} lessons</td>
                <td className="px-4 py-3 text-slate2 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-slate2 whitespace-nowrap">{fmtDate(u.last_login_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {!u.is_admin && (
                    confirmDelete === u.id ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-xs text-slate2">Remove?</span>
                        <button
                          type="button"
                          disabled={deleting}
                          onClick={() => handleDelete(u.id)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(null)}
                          className="text-xs text-slate2 hover:text-ink"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(u.id)}
                        className="text-slate2/40 hover:text-red-500 transition-colors"
                        title="Delete user"
                      >
                        <FaTrash className="text-xs" />
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate2 text-sm">No users match.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-slate2">{filtered.length} of {users.length} users</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: PROGRESS
// ────────────────────────────────────────────────────────────────────────────
function ProgressTab() {
  const [aggregate, setAggregate] = useState(null);
  const [users, setUsers] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchJSON('/api/admin/progress'),
      fetchJSON('/api/admin/users'),
    ]).then(([prog, usr]) => {
      setAggregate(prog.progress);
      setUsers(usr.users);
    }).catch((e) => setErr(e.message));
  }, []);

  const loadUserProgress = useCallback(async (userId) => {
    if (!userId) { setSelectedUser(null); setUserProgress(null); return; }
    setSelectedUser(userId);
    setUserProgress(null);
    setUserLoading(true);
    try {
      const d = await fetchJSON(`/api/admin/progress/user/${userId}`);
      setUserProgress(d.progress);
    } catch (e) {
      setErr(e.message);
    } finally {
      setUserLoading(false);
    }
  }, []);

  if (err) return <p className="text-red-700 text-sm">{err}</p>;
  if (!aggregate || !users) return <p className="text-slate2 text-sm">Loading…</p>;

  const selectedUserObj = users.find((u) => u.id === selectedUser);

  const renderAggregate = () => {
    const maxCompletions = Math.max(...aggregate.map((r) => parseInt(r.completions, 10)), 1);
    const byModule = aggregate.reduce((acc, r) => {
      if (!acc[r.module_title]) acc[r.module_title] = [];
      acc[r.module_title].push(r);
      return acc;
    }, {});
    return (
      <div className="space-y-8">
        {Object.entries(byModule).map(([mod, lessons]) => (
          <div key={mod}>
            <div className="font-display text-lg text-ink mb-3">{mod}</div>
            <div className="space-y-2">
              {lessons.map((l) => {
                const count = parseInt(l.completions, 10);
                const pct = Math.round((count / maxCompletions) * 100);
                return (
                  <div key={l.lesson_id} className="flex items-center gap-3 text-sm">
                    <div className="w-48 text-slate2 truncate flex-shrink-0">{l.lesson_title}</div>
                    <div className="flex-1 bg-ink/10 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-brand-deep h-2 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-10 text-right text-slate2 text-xs">{count}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderUserProgress = () => {
    if (userLoading) return <p className="text-slate2 text-sm">Loading…</p>;
    if (!userProgress) return null;
    const byModule = userProgress.reduce((acc, r) => {
      if (!acc[r.module_title]) acc[r.module_title] = [];
      acc[r.module_title].push(r);
      return acc;
    }, {});
    const total = userProgress.length;
    const done = userProgress.filter((r) => r.completed).length;
    return (
      <div>
        <div className="mb-4 flex items-center gap-4">
          <div className="text-sm text-slate2">
            <span className="font-medium text-ink">{done}</span> of {total} lessons completed
          </div>
          <div className="flex-1 bg-ink/10 rounded-full h-1.5 overflow-hidden max-w-xs">
            <div
              className="bg-brand-deep h-1.5 rounded-full"
              style={{ width: total ? `${Math.round((done / total) * 100)}%` : '0%' }}
            />
          </div>
          <div className="text-xs text-slate2">{total ? Math.round((done / total) * 100) : 0}%</div>
        </div>
        <div className="space-y-6">
          {Object.entries(byModule).map(([mod, lessons]) => (
            <div key={mod}>
              <div className="font-display text-base text-ink mb-2">{mod}</div>
              <div className="rounded-2xl border border-ink/10 divide-y divide-ink/5">
                {lessons.map((l) => (
                  <div key={l.lesson_id} className="flex items-center gap-3 px-4 py-2.5">
                    {l.completed
                      ? <FaCheck className="text-brand-deep text-xs flex-shrink-0" />
                      : <FaCircle className="text-ink/15 text-[8px] flex-shrink-0" />
                    }
                    <span className={`text-sm flex-1 ${l.completed ? 'text-ink' : 'text-slate2'}`}>
                      {l.lesson_title}
                    </span>
                    {l.completed && l.completed_at && (
                      <span className="text-xs text-slate2">{fmtDate(l.completed_at)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <label className="text-sm text-slate2 font-medium flex-shrink-0">View progress for:</label>
        <select
          value={selectedUser || ''}
          onChange={(e) => loadUserProgress(e.target.value ? parseInt(e.target.value, 10) : null)}
          className="rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-deep/60 min-w-[220px]"
        >
          <option value="">All users (aggregate)</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email} {u.name ? `(${u.email})` : ''}
            </option>
          ))}
        </select>
        {selectedUserObj && (
          <span className="text-xs text-slate2">
            Joined {fmtDate(selectedUserObj.created_at)}
            {selectedUserObj.purchase_status && (
              <StatusBadge status={selectedUserObj.purchase_status} />
            )}
          </span>
        )}
      </div>
      {!selectedUser ? renderAggregate() : renderUserProgress()}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: LESSON EDITOR
// ────────────────────────────────────────────────────────────────────────────
function LessonsTab() {
  const [lessons, setLessons] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    fetchJSON('/api/admin/lessons').then((d) => setLessons(d.lessons)).catch((e) => setErr(e.message));
  }, []);

  const openEditor = useCallback((lesson) => {
    setEditing(lesson);
    setDraft(lesson.body_md || '');
    setSaveMsg(null);
  }, []);

  const closeEditor = useCallback(() => {
    setEditing(null);
    setDraft('');
    setSaveMsg(null);
  }, []);

  const save = useCallback(async () => {
    if (!editing) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const result = await fetchJSON(`/api/admin/lessons/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body_md: draft }),
      });
      setSaveMsg({ ok: true, text: `Saved at ${new Date().toLocaleTimeString()}` });
      setLessons((prev) =>
        prev.map((l) => (l.id === editing.id ? { ...l, body_md: draft, updated_at: result.lesson.updated_at } : l))
      );
      setEditing((prev) => ({ ...prev, body_md: draft }));
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  }, [editing, draft]);

  if (err) return <p className="text-red-700 text-sm">{err}</p>;
  if (!lessons) return <p className="text-slate2 text-sm">Loading…</p>;

  if (editing) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
          <div>
            <div className="text-xs text-slate2">{editing.module_title}</div>
            <div className="font-display text-xl text-ink">{editing.title}</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {saveMsg && (
              <span className={`text-xs ${saveMsg.ok ? 'text-brand-deep' : 'text-red-600'}`}>
                {saveMsg.text}
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-slate2 hover:text-ink border border-ink/15 rounded-lg px-3 py-1.5"
            >
              <FaEye className="text-[10px]" />
              {showPreview ? 'Hide preview' : 'Show preview'}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              <FaFloppyDisk className="text-xs" />
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={closeEditor}
              className="inline-flex items-center gap-1.5 text-sm text-slate2 hover:text-ink"
            >
              <FaXmark /> Cancel
            </button>
          </div>
        </div>

        <div className="mb-2 flex items-start gap-4 text-xs text-slate2">
          <p>
            Supports <strong>Markdown</strong>: use <code className="bg-ink/5 px-1 rounded">**bold**</code>,{' '}
            <code className="bg-ink/5 px-1 rounded"># Heading</code>,{' '}
            <code className="bg-ink/5 px-1 rounded">- bullet</code>,{' '}
            <code className="bg-ink/5 px-1 rounded">[link text](https://…)</code>,{' '}
            <code className="bg-ink/5 px-1 rounded">![alt text](https://image-url)</code> for images.
            Images must be hosted online — paste the full URL.
          </p>
        </div>

        <div className={`grid gap-4 ${showPreview ? 'grid-cols-2' : 'grid-cols-1'}`} style={{ minHeight: '60vh' }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full h-full min-h-[60vh] rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm font-mono text-ink focus:outline-none focus:border-brand-deep/60 resize-none"
            spellCheck={false}
            placeholder="Write lesson content in Markdown…"
          />
          {showPreview && (
            <div className="rounded-xl border border-ink/10 bg-white px-6 py-4 overflow-y-auto min-h-[60vh] prose prose-sm max-w-none prose-headings:font-display prose-headings:text-ink prose-a:text-brand-deep prose-strong:text-ink">
              {draft.trim()
                ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft}</ReactMarkdown>
                : <p className="text-slate2 italic text-sm">Preview will appear here as you type…</p>
              }
            </div>
          )}
        </div>
      </div>
    );
  }

  const byModule = lessons.reduce((acc, l) => {
    const key = l.module_title;
    if (!acc[key]) acc[key] = [];
    acc[key].push(l);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <p className="text-sm text-slate2">
        Click <strong>Edit</strong> on any lesson to update its content. The editor shows a live preview alongside your writing.
        Images can be added using Markdown syntax with any publicly hosted image URL.
      </p>
      {Object.entries(byModule).map(([mod, ls]) => (
        <div key={mod}>
          <div className="font-display text-lg text-ink mb-3">{mod}</div>
          <div className="rounded-2xl border border-ink/10 divide-y divide-ink/5">
            {ls.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-4 py-3 gap-4 hover:bg-cream/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink truncate">{l.title}</div>
                  <div className="text-xs text-slate2 mt-0.5">
                    {l.slug}
                    {l.kind && l.kind !== 'lesson' && (
                      <span className="ml-2 text-[10px] bg-ink/10 text-slate2 px-1.5 py-0.5 rounded uppercase">{l.kind}</span>
                    )}
                    {l.is_preview && (
                      <span className="ml-1 text-[10px] bg-brand-sage/30 text-brand-deep px-1.5 py-0.5 rounded uppercase">free preview</span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-slate2 whitespace-nowrap flex-shrink-0">
                  {fmtDate(l.updated_at)}
                </div>
                <button
                  type="button"
                  onClick={() => openEditor(l)}
                  className="inline-flex items-center gap-1.5 text-xs text-brand-deep hover:text-ink font-medium flex-shrink-0"
                >
                  <FaPen className="text-[10px]" /> Edit
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: SURVEYS
// ────────────────────────────────────────────────────────────────────────────
function SurveysTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    fetchJSON('/api/admin/surveys').then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <p className="text-red-700 text-sm">{err}</p>;
  if (!data) return <p className="text-slate2 text-sm">Loading…</p>;

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatCard label="Responses" value={data.total} />
        <StatCard label="NPS Score" value={data.nps !== null ? data.nps : '—'} sub="net promoter score" />
        <StatCard label="Promoters" value={data.promoters} sub="scored 9–10" />
        <StatCard label="Detractors" value={data.detractors} sub="scored 0–6" />
      </div>
      <div className="space-y-4">
        {data.responses.map((r) => (
          <div key={r.id} className="rounded-2xl border border-ink/10 bg-white px-6 py-5">
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-display text-2xl text-brand-deep">{r.nps_score}</span>
                <span className="text-xs text-slate2">NPS score</span>
                <StatusBadge status={r.cohort} />
                {r.account_email && (
                  <span className="text-xs text-ink font-medium">
                    {r.account_name ? `${r.account_name} (${r.account_email})` : r.account_email}
                  </span>
                )}
              </div>
              <span className="text-xs text-slate2 shrink-0">{fmtDate(r.submitted_at)}</span>
            </div>
            {r.one_sentence && (
              <p className="text-sm text-ink leading-relaxed mb-2">
                <span className="font-medium">In their words:</span> "{r.one_sentence}"
              </p>
            )}
            {r.change_at_home && (
              <p className="text-sm text-slate2 leading-relaxed mb-2">
                <span className="font-medium text-ink">At home:</span> {r.change_at_home}
              </p>
            )}
            {r.one_thing_to_change && (
              <p className="text-sm text-slate2 leading-relaxed mb-2">
                <span className="font-medium text-ink">Change/add:</span> {r.one_thing_to_change}
              </p>
            )}
            {r.pilot_problems && (
              <p className="text-sm text-slate2 leading-relaxed mb-2">
                <span className="font-medium text-ink">Pilot issues:</span> {r.pilot_problems}
              </p>
            )}
            <div className="mt-2 flex items-center gap-3 text-xs text-slate2">
              {r.helpful_module && <span>Most helpful: <strong>{r.helpful_module}</strong></span>}
              {r.share_consent && r.share_consent !== 'no_share' && (
                <span>Share: <strong>{r.share_consent}</strong></span>
              )}
              {r.first_name && <span>Name: {r.first_name}</span>}
              {!r.account_email && r.email && <span>Email: {r.email}</span>}
            </div>
          </div>
        ))}
        {data.responses.length === 0 && (
          <p className="text-slate2 text-sm text-center py-8">No survey responses yet.</p>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: PURCHASES
// ────────────────────────────────────────────────────────────────────────────
function fmtCAD(cents) {
  if (!cents) return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);
}

function PurchasesTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    fetchJSON('/api/admin/purchases').then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <p className="text-red-700 text-sm">{err}</p>;
  if (!data) return <p className="text-slate2 text-sm">Loading…</p>;

  const { purchases, stats } = data;
  const totalRevenue = parseInt(stats.total_revenue_cents, 10) || 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total revenue" value={fmtCAD(totalRevenue)} sub="direct + gift" />
        <StatCard label="Direct purchases" value={parseInt(stats.direct_count, 10)} />
        <StatCard label="Gifted enrolments" value={parseInt(stats.gift_count, 10)} />
        <StatCard label="Access code (free)" value={parseInt(stats.pilot_count, 10)} />
      </div>

      {purchases.length === 0 ? (
        <p className="text-slate2 text-sm">No purchases yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream/60 border-b border-ink/10">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Date</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Type</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">From</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Recipient</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/5">
              {purchases.map((p) => {
                const isGift = p.status === 'gift';
                const isPilot = p.status === 'pilot';
                return (
                  <tr key={p.id} className="hover:bg-cream/40 transition-colors">
                    <td className="px-4 py-3 text-xs text-slate2 whitespace-nowrap">{fmtDate(p.purchased_at)}</td>
                    <td className="px-4 py-3">
                      {isGift ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-blue-50 text-blue-700">
                          <FaGift className="text-[9px]" /> Gift
                        </span>
                      ) : isPilot ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-brand-aubergine/10 text-brand-aubergine">Access Code</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-brand-sage/30 text-brand-deep">Direct</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isGift && p.purchaser_email ? (
                        <div>
                          <div className="text-ink text-xs">{p.purchaser_name || <span className="italic text-slate2">—</span>}</div>
                          <div className="text-slate2 text-[11px]">{p.purchaser_email}</div>
                        </div>
                      ) : (
                        <div className="text-slate2 text-xs italic">self</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-ink text-xs">{p.recipient_name || <span className="italic text-slate2">—</span>}</div>
                      <div className="text-slate2 text-[11px]">{p.recipient_email}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-ink font-medium whitespace-nowrap">
                      {isPilot ? <span className="text-slate2">Free</span> : fmtCAD(p.amount_cents)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: PILOT CODES
// ────────────────────────────────────────────────────────────────────────────
function PilotCodesTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    fetchJSON('/api/admin/pilot-codes').then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <p className="text-red-700 text-sm">{err}</p>;
  if (!data) return <p className="text-slate2 text-sm">Loading…</p>;
  return (
    <div className="rounded-2xl border border-ink/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-cream/60 border-b border-ink/10">
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Code</th>
            <th className="text-right px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Used</th>
            <th className="text-right px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Max</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Expires</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink/5">
          {data.codes.map((c) => (
            <tr key={c.id} className="hover:bg-cream/40 transition-colors">
              <td className="px-4 py-3 font-mono text-ink font-medium">{c.code}</td>
              <td className="px-4 py-3 text-right text-ink">{c.use_count}</td>
              <td className="px-4 py-3 text-right text-slate2">{c.max_uses}</td>
              <td className="px-4 py-3 text-slate2">{c.expires_at ? fmtDate(c.expires_at) : 'Never'}</td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide ${c.is_active ? 'bg-brand-sage/30 text-brand-deep' : 'bg-ink/10 text-slate2'}`}>
                  {c.is_active ? 'active' : 'inactive'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: DOMAIN ACCESS
// ────────────────────────────────────────────────────────────────────────────
function DomainGrantsTab() {
  const [data, setData]       = useState(null);
  const [err, setErr]         = useState(null);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({ domain: '', label: '', course_slug: 'ai-for-parents', expires_at: '' });
  const [formErr, setFormErr] = useState(null);

  const load = useCallback(() => {
    fetchJSON('/api/admin/domain-grants').then(setData).catch((e) => setErr(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormErr(null);
    if (!form.domain.trim()) { setFormErr('Domain is required.'); return; }
    setSaving(true);
    try {
      await fetchJSON('/api/admin/domain-grants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain:      form.domain.trim().toLowerCase().replace(/^@/, ''),
          label:       form.label.trim() || null,
          course_slug: form.course_slug,
          expires_at:  form.expires_at || null,
        }),
      });
      setForm({ domain: '', label: '', course_slug: 'ai-for-parents', expires_at: '' });
      load();
    } catch (e) {
      setFormErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this domain grant? Existing enrollments are kept.')) return;
    try {
      await fetchJSON(`/api/admin/domain-grants/${id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  if (err) return <p className="text-red-700 text-sm">{err}</p>;

  return (
    <div className="space-y-8">
      {/* Add form */}
      <div className="rounded-2xl border border-ink/10 bg-white p-6">
        <h3 className="font-display text-lg text-ink mb-4">Add Domain Grant</h3>
        <p className="text-sm text-slate2 mb-5">
          Anyone who signs in with a matching email domain will be automatically enrolled at no charge.
          Existing users with that domain are back-filled immediately.
        </p>
        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate2 mb-1">Email domain <span className="text-red-500">*</span></label>
            <input
              type="text"
              placeholder="tdsb.on.ca"
              value={form.domain}
              onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
              className="w-full border border-ink/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-deep/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate2 mb-1">Label (optional)</label>
            <input
              type="text"
              placeholder="Toronto District School Board"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className="w-full border border-ink/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-deep/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate2 mb-1">Course</label>
            <input
              type="text"
              value={form.course_slug}
              onChange={(e) => setForm((f) => ({ ...f, course_slug: e.target.value }))}
              className="w-full border border-ink/20 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-deep/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate2 mb-1">Expires (optional)</label>
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
              className="w-full border border-ink/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-deep/30"
            />
          </div>
          {formErr && <p className="sm:col-span-2 text-red-600 text-xs">{formErr}</p>}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary text-sm px-5 py-2 disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add domain grant'}
            </button>
          </div>
        </form>
      </div>

      {/* Existing grants */}
      {!data ? (
        <p className="text-slate2 text-sm">Loading…</p>
      ) : data.grants.length === 0 ? (
        <p className="text-slate2 text-sm">No domain grants configured yet.</p>
      ) : (
        <div className="rounded-2xl border border-ink/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream/60 border-b border-ink/10">
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Domain</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Label</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Course</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Enrolled</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-slate2 font-medium">Expires</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/5">
              {data.grants.map((g) => (
                <tr key={g.id} className="hover:bg-cream/40 transition-colors">
                  <td className="px-4 py-3 font-mono text-ink">@{g.domain}</td>
                  <td className="px-4 py-3 text-slate2">{g.label || '—'}</td>
                  <td className="px-4 py-3 text-slate2 font-mono text-xs">{g.course_slug}</td>
                  <td className="px-4 py-3 text-right text-ink">{g.enrolled_count}</td>
                  <td className="px-4 py-3 text-slate2">{g.expires_at ? fmtDate(g.expires_at) : 'Never'}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(g.id)}
                      className="text-slate2 hover:text-red-600 transition-colors"
                      title="Remove domain grant"
                    >
                      <FaTrash className="text-xs" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ────────────────────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login?next=/admin', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="container-page py-24 text-center text-slate2 text-sm">Loading…</div>
    );
  }

  if (!user) return null;

  if (!user.is_admin) {
    return (
      <div className="container-page py-24 text-center">
        <div className="font-display text-2xl text-ink mb-3">Access denied</div>
        <p className="text-slate2 text-sm">This page requires admin access.</p>
        <Link to="/" className="btn-link mt-6 inline-flex">Back to home</Link>
      </div>
    );
  }

  const tabContent = {
    overview:  <OverviewTab />,
    users:     <UsersTab />,
    progress:  <ProgressTab />,
    lessons:   <LessonsTab />,
    surveys:   <SurveysTab />,
    purchases: <PurchasesTab />,
    pilots:    <PilotCodesTab />,
    domains:   <DomainGrantsTab />,
  };

  return (
    <div className="w-full bg-bone min-h-screen">
      <div className="border-b border-ink/10 bg-bone">
        <div className="container-page py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-slate2 hover:text-ink">
              <FaArrowLeft className="text-xs" />
            </Link>
            <span className="font-display text-xl text-ink">Admin Dashboard</span>
            <span className="text-[10px] tracking-[0.18em] uppercase bg-brand-deep text-bone px-2 py-0.5 rounded-full">
              Secure
            </span>
          </div>
          <span className="text-xs text-slate2">{user.email}</span>
        </div>
      </div>

      <div className="border-b border-ink/10 bg-bone">
        <div className="container-page flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                `inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap ` +
                (tab === t.id
                  ? 'border-brand-deep text-ink font-medium'
                  : 'border-transparent text-slate2 hover:text-ink')
              }
            >
              <span className="text-xs">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="container-page py-8">
        {tabContent[tab]}
      </div>
    </div>
  );
}
